import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Form } from 'src/shared/components/ui/forms/form'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import { checkUsernameAvailability } from 'src/shared/features/users/api/accounts/check-username-available'
import type { UsernameFormData } from 'src/shared/features/users/schemas/user'
import { usernameFormSchema } from 'src/shared/features/users/schemas/user'
import { useEventStore } from 'src/shared/store/event-store'

type UsernameStepProps = {
  onNext: (data: UsernameFormData) => void
  initialData?: Partial<UsernameFormData>
  additionalLabel?: string
}

const UsernameStep = ({ onNext, initialData, additionalLabel }: UsernameStepProps) => {
  const form = useForm<UsernameFormData>({
    resolver: zodResolver(usernameFormSchema),
    defaultValues: { username: initialData?.username?.toLowerCase().trim() || '' },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasInitializedRef = useRef(false)

  // Watch for username changes and force lowercase and trim whitespace
  const username = form.watch('username')
  useEffect(() => {
    if (username) {
      const trimmedLowercase = username.toLowerCase().trim()
      if (trimmedLowercase !== username) {
        form.setValue('username', trimmedLowercase, { shouldValidate: true })
      }
    }
  }, [username, form])

  // Access only what we need from event store
  const anonymousId = useEventStore((state) => state.anonymousId)
  const sessionId = useEventStore((state) => state.sessionId)
  const flowId = useEventStore((state) => state.flowId)
  const setAnonymousId = useEventStore((state) => state.setAnonymousId)
  const setSessionId = useEventStore((state) => state.setSessionId)
  const setFlowId = useEventStore((state) => state.setFlowId)

  // Setup event IDs at component mount time, only once
  useEffect(() => {
    if (hasInitializedRef.current) return

    if (!anonymousId) {
      setAnonymousId()
    }
    if (!sessionId) {
      setSessionId()
    }
    if (!flowId) {
      setFlowId()
    }

    hasInitializedRef.current = true
  }, [anonymousId, sessionId, flowId, setAnonymousId, setSessionId, setFlowId])

  const onSubmit = async (data: UsernameFormData) => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      // Ensure username is trimmed and lowercase before submission
      const formattedData = {
        username: data.username.toLowerCase().trim(),
      }

      const isAvailable = await checkUsernameAvailability(formattedData.username)
      if (isAvailable) {
        // Get the latest values directly from the store
        const currentAnonymousId = useEventStore.getState().anonymousId
        const currentSessionId = useEventStore.getState().sessionId
        const currentFlowId = useEventStore.getState().flowId

        if (currentAnonymousId && currentSessionId && currentFlowId) {
          sendEvent({
            anonymousId: currentAnonymousId,
            eventType: 'flow_step',
            info: {
              ...formattedData,
              flowType: 'onboarding',
              flowId: currentFlowId,
              sessionId: currentSessionId,
              flowStep: 'submit_username',
            },
          })
          onNext(formattedData)
        }
      } else {
        form.setError('username', { message: 'Username is already taken' })
      }
    } catch (error) {
      console.error('Error checking username availability:', error)
      form.setError('username', { message: 'Unable to check username, please try again later' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <AuthInputFormField control={form.control} name="username" label="Username" />
        <p className="mt-4 text-sm text-muted">{additionalLabel}</p>
        <AuthSubmitButton type="submit" isLoading={isSubmitting} className="mt-[25px] bg-primary">
          Continue
        </AuthSubmitButton>
      </form>
    </Form>
  )
}

UsernameStep.displayName = 'UsernameStep'

export default UsernameStep
